// Source-agnostic pricing config accessor (D14).
// The plan ladder is sourced live from Stripe via GET /billing/v1/checkout/packages
// (PLT-4162); the bundled stub below is only a loading/offline fallback so this
// hook keeps returning a PricingConfig *synchronously* for its many consumers.

import { useQuery } from '@tanstack/react-query';
import { BillingClient } from 'network/httpClients/BillingClient/BillingClient';
import { QUERY_KEYS } from 'network/queries/consts';
import { isMigratedPaidUser } from 'utils/billing/conversion';
import { useAccountInfo } from './useAccountInfo';
import { useSelfServeGate } from './useSelfServeGate';

/**
 * A self-serve plan SKU identifier. This is the plan's monthly credit count as
 * a string (e.g. "5000") — the value POST /billing/v1/checkout/session expects
 * (PLT-4163) and the identity the packages endpoint keys on. Loosely typed as
 * string because the live ladder comes from Stripe, not a compile-time set.
 */
export type PlanSkuId = string;

export interface PlanSku {
  sku: PlanSkuId;
  priceMonthly: number;
  credits: number;
  // Backend-only off the live ladder; never sent to the FE. Optional so the
  // stub fallback need not carry fake ids.
  stripePriceId?: string;
}

export interface PricingConfig {
  ratePerCredit: number;
  reviewsPerPr: number;
  creditsPerReview: number;
  plans: PlanSku[];
  // True only once the live Stripe ladder has loaded (PLT-4162). Stays false in
  // the stub (loading / offline / failure / non-self-serve) so the popup can
  // show a loading state and keep checkout disabled instead of POSTing an
  // empty/stale SKU against an empty ladder.
  isPlansReady: boolean;
  enterpriseHandoff: { engineers: number; monthlySpend: number };
  alertThresholds: {
    approaching: number[];
  };
  trialEndingThresholdDays: number;
}

// Fallback for the static, API-independent fields only — `ratePerCredit`,
// thresholds, `creditsPerReview`, etc. that /checkout/packages doesn't return
// and consumers (calculator, banner, alerts) read synchronously.
// `plans` is intentionally EMPTY: hardcoded rungs aren't real Stripe ladder
// values, so checking out against them would 400 (Shlomo) — the carousel only
// ever renders the live ladder from /checkout/packages, staying empty until it
// loads rather than offering un-buyable rungs.
const PRICING_STUB: PricingConfig = {
  ratePerCredit: 0.012,
  reviewsPerPr: 1.2, // CONFIRMED Shlomo — enterprise avg
  creditsPerReview: 140,
  plans: [],
  isPlansReady: false,
  enterpriseHandoff: { engineers: 30, monthlySpend: 2000 },
  alertThresholds: { approaching: [80, 85, 90, 95] },
  trialEndingThresholdDays: 3,
};

/**
 * Returns the current pricing configuration.
 *
 * The plan ladder is fetched live from Stripe via GET /billing/v1/checkout/packages
 * (PLT-4162) — the same source checkout resolves the billed price against, so
 * the displayed ladder can't drift from what gets charged. Returns synchronously
 * (consumers destructure it directly): until the fetch resolves — or if it
 * fails, or for non-self-serve users who never fetch — the stub supplies the
 * static fields (rate/thresholds) with an empty `plans`, so the contract never
 * yields `undefined` and the carousel never shows un-buyable rungs.
 */
export const usePricingConfig = (): PricingConfig => {
  // Only self-serve (FF-on, non-enterprise) users need the live ladder and may
  // hit the self-serve endpoint. Enterprise / FF-off consumers (the top-bar
  // pill, banner, calculator) only read the static rate/thresholds below, so
  // they get the stub with NO network call — keeps this additive and leaves the
  // enterprise path untouched (would otherwise fire /checkout/packages for
  // everyone mounting the pill).
  // Paid migration users (selfServe=false, awaiting_move) also need the ladder
  // to select a plan in the ConversionPopup.
  const { allowed } = useSelfServeGate();
  const { data: accountInfo } = useAccountInfo();
  const isMigratedPaid = isMigratedPaidUser(accountInfo);

  const { data } = useQuery({
    // Global, static catalog — cache hard and don't refetch on focus.
    queryKey: [QUERY_KEYS.BILLING_PACKAGES],
    queryFn: BillingClient.getCheckoutPackages,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: allowed || isMigratedPaid,
  });

  if (!data?.packages?.length) {
    return PRICING_STUB;
  }

  const plans: PlanSku[] = [...data.packages]
    .sort((a, b) => a.credits - b.credits)
    .map((pkg) => ({
      sku: String(pkg.credits),
      credits: pkg.credits,
      priceMonthly: pkg.monthlyUsd,
    }));

  // Real ladder for the carousel + checkout; FE-only tuning fields from the stub.
  return { ...PRICING_STUB, plans, isPlansReady: true };
};
